AFRAME.registerComponent('news-graph-2d-vr', {
    init: function () {
        this.selectedNodeEl  = null;
        this.hoveredNodeEl   = null;
        this._nodeClickedThisFrame = false;

        this._onSetGlobeVisible = (e) => {
            const visible = !!(e && e.detail && e.detail.visible);
            if (this._separationRing) this._separationRing.visible = visible;
        };
        this.el.sceneEl.addEventListener('news-graph-set-globe-visible', this._onSetGlobeVisible);
    },

    remove: function () {
        if (this.selectedNodeEl) {
            this.setSelectedNodeEl(null, null, false);
        }
        const scene = this.el.sceneEl || document.querySelector('a-scene');
        if (this._onThemeChanged) {
            if (scene) {
                scene.removeEventListener('theme-changed', this._onThemeChanged);
                scene.removeEventListener('app-state-changed', this._onThemeChanged);
            }
            document.removeEventListener('theme-changed', this._onThemeChanged);
            window.removeEventListener('theme-changed', this._onThemeChanged);
        }
        if (this.el.sceneEl) {
            this.el.sceneEl.removeEventListener('news-graph-set-globe-visible', this._onSetGlobeVisible);
        }
        if (this._edgesGroup) {
            if (this.el.object3D) this.el.object3D.remove(this._edgesGroup);
            this._edgesGroup = null;
        }
        if (this._sectorEl) {
            this._sectorEl.remove();
            this._sectorEl = null;
        }
        this._destroyLabels();
        try { this.el.replaceChildren(); } catch (e) {}
    },

    mount: function (data) {
        this.graphData = data;
        this.selectedNodeEl = null;
        this.hoveredNodeEl = null;
        if (this._edgesGroup && this.el.object3D) {
            this.el.object3D.remove(this._edgesGroup);
            this._edgesGroup = null;
        }
        if (this._sectorEl) {
            this._sectorEl.remove();
            this._sectorEl = null;
        }
        this.el.replaceChildren();
        this._destroyLabels();

        this.nodes = window.NewsGraph.selectNodes(data);
        const links = window.NewsGraph.selectLinks(data.relationships || [], this.nodes);
        this.links  = links;

        this.tickerPriceMap = window.NewsProxyAPIManager.buildTickerPriceMap(data);
        this.preIpoSymbols  = new Set(
            (data?.tickers || [])
                .filter(t => t.possible_pre_ipo === true)
                .map(t => String(t.symbol || '').toUpperCase().trim())
        );

        const buildResult = window.NewsGraph.NewsGraphBuilder.buildNodes(this.nodes, this.el, false, links);
        this.meshMap  = buildResult.meshMap;
        this.nodeEls  = buildResult.nodeEls;
        this.coneInfos = buildResult.coneInfos;

        this._replaceGeometriesWithCircles();

        const layout = window.NewsGraph.compute2DLayout(this.nodes, data);
        this._layout = layout;

        this._applyTickerRingPositions(layout);
        this._applyNarrativeRingPositions(layout);
        this._buildSectorArcs(layout);
        this._buildSeparationRing();
        this._buildEdges2D(links);
        this._buildVRLabels();
        this._attachNodeInteraction();
        this._updateNodeVisuals();
        this._attachThemeListener();
        this._updateLabelTheme();

        this._adjustScaleToFit();

        this._autoRotate = true;
        this._lastTime   = null;
    },

    _applyTickerRingPositions: function (layout) {
        const OUTER_R = 4.0;
        const positions = window.NewsGraph.computeTickerRingPositions(layout.sortedTickerNodes, OUTER_R);
        positions.forEach(({ node, x, y, angle }) => {
            const el = this.meshMap[node.id];
            if (!el) return;
            el.object3D.position.set(x, y, 0);
            const isFlipped = window.NewsGraph.isConeFlipped(node, this.tickerPriceMap, this.preIpoSymbols);
            // CircleGeometry(…, 3) points along +X at zero rotation.
            el.userData.staticConeRotation = Math.PI / 2 + (isFlipped ? Math.PI : 0);
            el.object3D.rotation.set(0, 0, el.userData.staticConeRotation);
            node._angle2D = angle;
        });
    },

    _applyNarrativeRingPositions: function (layout) {
        const INNER_R = 2.0;
        const positions = window.NewsGraph.computeNarrativeRingPositions(layout.narrativeNodes, INNER_R);
        positions.forEach(({ node, x, y, angle }) => {
            const el = this.meshMap[node.id];
            if (!el) return;
            el.object3D.position.set(x, y, 0);
            el.object3D.rotation.set(0, 0, 0);
            node._angle2D = angle;
        });
    },

    _getLabelsContainer: function () {
        if (!this.labelsContainer) {
            this.labelsContainer = document.createElement('a-entity');
            this.labelsContainer.setAttribute('id', 'news-graph-2d-labels-container');
            this.el.appendChild(this.labelsContainer);
        }
        return this.labelsContainer;
    },

    _buildSectorArcs: function (layout) {
        const THREE = window.THREE || AFRAME.THREE;
        const arcs  = window.NewsGraph.computeSectorArcs(
            layout.sortedTickerNodes,
            layout.sectorsMap,
            layout.activeSectorProxies
        );

        const sectorEl = document.createElement('a-entity');
        sectorEl.setAttribute('id', 'news-graph-2d-sectors');
        this.el.appendChild(sectorEl);
        this._sectorEl = sectorEl;

        this._sectorHelpers = [];

        const BORDER_SEGMENTS = 32;
        const INNER_R = 3.65;
        const OUTER_R = 4.35;

        arcs.forEach(arc => {
            const pad = Math.min(0.03, arc.arcLen * 0.1);
            const thetaStart = arc.startAngle + pad;
            const thetaLength = Math.max(0.02, arc.arcLen - pad * 2);

            const ringGeo = new THREE.RingGeometry(INNER_R, OUTER_R, 32, 1, thetaStart, thetaLength);
            const ringMat = new THREE.MeshBasicMaterial({
                color: new THREE.Color(arc.color),
                transparent: true,
                opacity: 0.28,
                side: THREE.DoubleSide,
                depthWrite: false,
                depthTest: false,
            });
            const bandMesh = new THREE.Mesh(ringGeo, ringMat);
            bandMesh.position.z = -0.02;
            bandMesh.renderOrder = 0;
            sectorEl.object3D.add(bandMesh);

            const outerPts = [];
            const innerPts = [];
            for (let s = 0; s <= BORDER_SEGMENTS; s++) {
                const a = thetaStart + (s / BORDER_SEGMENTS) * thetaLength;
                outerPts.push(new THREE.Vector3(OUTER_R * Math.cos(a), OUTER_R * Math.sin(a), -0.01));
                innerPts.push(new THREE.Vector3(INNER_R * Math.cos(a), INNER_R * Math.sin(a), -0.01));
            }
            const borderMat = new THREE.LineBasicMaterial({
                color: new THREE.Color(arc.color),
                transparent: true,
                opacity: 0.55,
            });
            const outerLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints(outerPts), borderMat);
            const innerLine = new THREE.Line(new THREE.BufferGeometry().setFromPoints(innerPts), borderMat);
            outerLine.renderOrder = 1;
            innerLine.renderOrder = 1;
            sectorEl.object3D.add(outerLine);
            sectorEl.object3D.add(innerLine);

            const helper = new THREE.Object3D();
            helper.position.set(4.6 * Math.cos(arc.midAngle), 4.6 * Math.sin(arc.midAngle), 0);
            sectorEl.object3D.add(helper);

            this._sectorHelpers.push({ helper, arc });
        });

        this._sectorLabelEls = [];
        const container = this._getLabelsContainer();
        this._sectorHelpers.forEach(({ helper, arc }) => {
            const textEl = document.createElement('a-troika-text');
            textEl.setAttribute('value', arc.name.toUpperCase());
            textEl.setAttribute('color', arc.color);
            textEl.setAttribute('font-size', '0.015');
            textEl.setAttribute('align', 'center');
            textEl.setAttribute('anchor', 'center');
            textEl.setAttribute('outline-color', '#000000');
            textEl.setAttribute('outline-width', '0.003');
            textEl.setAttribute('depth-offset', '-1');
            container.appendChild(textEl);
            this._sectorLabelEls.push({ textEl, helper, arc });
        });
    },

    _buildEdges2D: function (links) {
        const THREE  = window.THREE || AFRAME.THREE;
        const group  = new THREE.Group();
        this.el.object3D.add(group);
        this._edgesGroup = group;

        const quadGeo = new THREE.PlaneGeometry(1, 1);

        links.forEach(link => {
            const thickness = 0.02 + (link.strength / 100) * 0.04;
            const opacity   = 0.28 + (link.strength / 100) * 0.38;
            const mat  = new THREE.MeshBasicMaterial({
                color: 0x94a3b8,
                transparent: true,
                opacity,
                depthWrite: false,
            });
            const mesh = new THREE.Mesh(quadGeo, mat);
            mesh.userData = {
                sourceId: link.source,
                targetId: link.target,
                thickness,
                baseOpacity: opacity,
            };
            mesh.renderOrder = 1;
            group.add(mesh);
        });
    },

    _buildSeparationRing: function () {
        const THREE = window.THREE || AFRAME.THREE;
        const points = [];
        const radius = 2.95;
        const segments = 128;
        for (let i = 0; i <= segments; i += 1) {
            const angle = (i / segments) * Math.PI * 2;
            points.push(new THREE.Vector3(
                radius * Math.cos(angle),
                radius * Math.sin(angle),
                0
            ));
        }
        this._separationRing = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(points),
            new THREE.LineDashedMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: 0.9,
                dashSize: 0.14,
                gapSize: 0.10,
                depthWrite: false,
                depthTest: false
            })
        );
        this._separationRing.computeLineDistances();
        this._separationRing.position.z = -0.01;
        try {
            const settings = typeof getCacheItem === 'function'
                ? getCacheItem('configureControlSettings')
                : null;
            const actualSettings = settings ? (settings.value || settings) : null;
            if (actualSettings && typeof actualSettings.isGlobeVisible !== 'undefined') {
                this._separationRing.visible = !!actualSettings.isGlobeVisible;
            }
        } catch (e) {
            console.warn('Failed to load 2D news ring visibility:', e);
        }
        this.el.object3D.add(this._separationRing);
    },

    _buildVRLabels: function () {
        this.labelData = [];
        const container = this._getLabelsContainer();

        this.nodes.forEach(node => {
            const meshEl   = this.meshMap[node.id];
            if (!meshEl) return;

            const isNarrative = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
            let raw = node.label || node.id;
            if (!isNarrative) raw = raw.split(' - ')[0].trim().toUpperCase();
            const truncated = raw.length > 32 ? raw.slice(0, 32) + '…' : raw;

            const textEl = document.createElement('a-troika-text');
            textEl.setAttribute('value', truncated);
            textEl.setAttribute('color', '#ffffff');
            textEl.setAttribute('font-size', isNarrative ? '0.018' : '0.012');
            textEl.setAttribute('outline-color', '#000000');
            textEl.setAttribute('outline-width', isNarrative ? '0.003' : '0.002');
            textEl.setAttribute('align', 'center');
            textEl.setAttribute('depth-offset', '-1');
            textEl.setAttribute('max-width', isNarrative ? '0.5' : '0.3');
            textEl.addEventListener('object3dset', () => {
                if (textEl.object3D) textEl.object3D.renderOrder = 9999;
            });

            container.appendChild(textEl);
            this.labelData.push({ meshEl, textEl, r: meshEl.userData.radius ?? 0, isNarrative });
        });
    },

    _attachNodeInteraction: function () {
        this.nodeEls.forEach(el => {
            if (el.hasAttribute('data-raycastable')) return;
            el.setAttribute('data-raycastable', '');

            el.addEventListener('click', () => {
                this._nodeClickedThisFrame = true;
                const node = this.nodes.find(n => this.meshMap[n.id] === el);
                this.setSelectedNodeEl(this.selectedNodeEl === el ? null : el, node);
            });
            el.addEventListener('mouseenter', () => {
                this.hoveredNodeEl = el;
                this._updateNodeVisuals();
            });
            el.addEventListener('mouseleave', () => {
                this.hoveredNodeEl = null;
                this._updateNodeVisuals();
            });
        });
    },

    _replaceGeometriesWithCircles: function () {
        const THREE = window.THREE || AFRAME.THREE;
        const counts = this.nodes.map(n => n.article_count || 0);
        const minC = Math.min(...counts);
        const maxC = Math.max(...counts);

        this.nodeEls.forEach(el => {
            const node = this.nodes.find(n => this.meshMap[n.id] === el);
            if (!node) return;

            const isNarrative = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
            const baseRadius = window.NewsGraph.nodeRadius(node.article_count || 0, minC, maxC);
            const r = baseRadius * (isNarrative ? 0.38 : 0.25);

            const colorStr = window.NewsGraph.getNodeColor(node, this.tickerPriceMap, this.preIpoSymbols);
            const baseOpacity = isNarrative ? 0.90 : 0.75;

            const geo = new THREE.CircleGeometry(r, isNarrative ? 32 : 3);
            const mat = new THREE.MeshBasicMaterial({
                color: new THREE.Color(colorStr),
                transparent: true,
                opacity: baseOpacity,
                side: THREE.DoubleSide,
                depthWrite: false
            });
            const mesh = new THREE.Mesh(geo, mat);
            mesh.renderOrder = 2;

            el.removeAttribute('geometry');
            el.removeAttribute('material');

            el.setObject3D('mesh', mesh);

            el.userData.radius = r;
            el.userData.is2DCircle = isNarrative;
            el.userData.is2DTriangle = !isNarrative;

            const newGlowScale = r * 3.2;
            el.userData.baseGlowScale = newGlowScale;
            const glowSprite = el.getObject3D('glowSprite');
            if (glowSprite) {
                glowSprite.scale.set(newGlowScale, newGlowScale, 1);
                glowSprite.material.opacity = 0.0;
            }
        });
    },

    _updateSelectionOutline2D: function (el, r, isSelected, isNarrative) {
        if (isSelected) {
            let outlineEl = el.querySelector('.selection-outline-2d');
            if (!outlineEl) {
                const old = el.querySelector('.selection-outline');
                if (old) old.remove();

                outlineEl = document.createElement('a-entity');
                outlineEl.classList.add('selection-outline-2d');
                outlineEl.setAttribute('geometry', `primitive: ring; radiusInner: ${(r || 0.5) * 1.05}; radiusOuter: ${(r || 0.5) * 1.18}; segmentsTheta: ${isNarrative ? 32 : 3}`);
                outlineEl.setAttribute('material', {
                    color: '#00ffff',
                    side: 'double',
                    transparent: true,
                    opacity: 0.85,
                    shader: 'flat',
                    depthWrite: false
                });
                outlineEl.object3D.position.set(0, 0, 0.005);
                el.appendChild(outlineEl);
            }
        } else {
            const outlineEl = el.querySelector('.selection-outline-2d');
            if (outlineEl) outlineEl.remove();
            const old = el.querySelector('.selection-outline');
            if (old) old.remove();
        }
    },

    _updateNodeVisuals: function () {
        if (!this.meshMap || !this.nodes) return;

        let selectedNodeId = null;
        if (this.selectedNodeEl) {
            selectedNodeId = Object.keys(this.meshMap).find(id => this.meshMap[id] === this.selectedNodeEl) || null;
        }
        const highlightedIds = this._getHighlightedIds(selectedNodeId);

        this.nodes.forEach(node => {
            const el = this.meshMap[node.id];
            if (!el) return;

            const isSphere   = window.NewsGraph.NARRATIVE_TYPES.has(node.type);
            const isHovered  = (el === this.hoveredNodeEl);
            const isSelected = (el === this.selectedNodeEl);

            const isDimmed = !!highlightedIds && !highlightedIds.has(node.id);
            const meshObj  = el.getObject3D('mesh');
            if (meshObj && meshObj.material) {
                meshObj.renderOrder = 10;
                const baseColorStr = window.NewsGraph.getNodeColor(node, this.tickerPriceMap, this.preIpoSymbols);
                meshObj.material.color.set(baseColorStr);
                meshObj.material.opacity = isDimmed ? 0.15 : (isSphere ? 0.90 : 0.75);
                meshObj.material.depthWrite = !isDimmed;
                meshObj.material.depthTest  = true;
                meshObj.material.needsUpdate = true;
            }

            const isFresh = node.freshness &&
                (node.freshness.status === 'inflection' || node.freshness.status === 'new');
            el.userData.isFresh      = !!isFresh && !isDimmed;
            if (isFresh) {
                el.userData.emissiveMin = 0.05;
                el.userData.emissiveMax = 0.6;
            }

            this._updateSelectionOutline2D(el, el.userData.radius, isSelected, isSphere);
        });

        if (this._edgesGroup) {
            this._edgesGroup.children.forEach(mesh => {
                if (!mesh.material) return;
                mesh.renderOrder = 10;
                if (!selectedNodeId) {
                    mesh.material.color.set(0x94a3b8);
                    mesh.material.opacity = mesh.userData.baseOpacity || 0.5;
                } else {
                    const isConn =
                        mesh.userData.sourceId === selectedNodeId ||
                        mesh.userData.targetId === selectedNodeId;
                    mesh.material.color.set(isConn ? 0x38bdf8 : 0x94a3b8);
                    mesh.material.opacity = isConn
                        ? Math.min(1.0, (mesh.userData.baseOpacity || 0.5) + 0.35)
                        : 0.1;
                }
                mesh.material.depthTest  = true;
                mesh.material.needsUpdate = true;
            });
        }
    },

    _getHighlightedIds: function (selectedNodeId) {
        if (!selectedNodeId) return null;
        const ids = new Set([selectedNodeId]);
        (this.links || []).forEach(link => {
            if (link.source === selectedNodeId) ids.add(link.target);
            if (link.target === selectedNodeId) ids.add(link.source);
        });
        return ids;
    },

    _updateEdges2D: function () {
        if (!this._edgesGroup) return;
        const THREE = window.THREE || AFRAME.THREE;
        const v1 = new THREE.Vector3();
        const v2 = new THREE.Vector3();

        this._edgesGroup.children.forEach(mesh => {
            const elA = this.meshMap[mesh.userData.sourceId];
            const elB = this.meshMap[mesh.userData.targetId];
            if (!elA || !elB) return;

            elA.object3D.getWorldPosition(v1);
            elB.object3D.getWorldPosition(v2);
            this._edgesGroup.worldToLocal(v1);
            this._edgesGroup.worldToLocal(v2);

            const len   = v1.distanceTo(v2);
            const angle = Math.atan2(v2.y - v1.y, v2.x - v1.x);
            mesh.position.set((v1.x + v2.x) / 2, (v1.y + v2.y) / 2, -0.015);
            mesh.rotation.z = angle;
            mesh.scale.set(len, mesh.userData.thickness, 1);
        });
    },

    _updateSectorLabels: function () {
        if (!this._sectorLabelEls) return;
        const THREE = window.THREE || AFRAME.THREE;

        this._sectorLabelEls.forEach(({ textEl, helper, arc }) => {
            if (!helper || !textEl.object3D) return;
            const helperWorld = new THREE.Vector3();
            helper.getWorldPosition(helperWorld);

            const localPos = textEl.object3D.parent ? textEl.object3D.parent.worldToLocal(helperWorld) : helperWorld;
            textEl.object3D.position.copy(localPos);

            const rotZ = this._sectorEl && this._sectorEl.object3D ? this._sectorEl.object3D.rotation.z : 0;
            const currentAngle = arc.midAngle + rotZ;
            let tangentAngle = currentAngle + Math.PI / 2;

            let normAngle = (tangentAngle % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
            if (normAngle > Math.PI / 2 && normAngle < (3 * Math.PI) / 2) {
                tangentAngle += Math.PI;
            }

            textEl.object3D.rotation.set(0, 0, tangentAngle);
        });
    },

    _updateVRLabels: function () {
        if (!this.labelData || this.labelData.length === 0) return;
        const THREE    = window.THREE || AFRAME.THREE;
        const cameraEl = document.querySelector('[camera]');
        if (!cameraEl || !cameraEl.object3D) return;
        const cameraPos = new THREE.Vector3();
        cameraEl.object3D.getWorldPosition(cameraPos);

        this.labelData.forEach(({ meshEl, textEl, r }) => {
            if (!meshEl.object3D || !textEl.object3D || !textEl.object3D.parent) return;

            const nodeWorldPos = new THREE.Vector3();
            meshEl.object3D.getWorldPosition(nodeWorldPos);

            const worldScaleVec = new THREE.Vector3();
            this.el.object3D.getWorldScale(worldScaleVec);
            const worldRadius = r * worldScaleVec.y;

            const GAP      = Math.max(0.015, worldRadius * 0.20);
            const toCamera = new THREE.Vector3().subVectors(cameraPos, nodeWorldPos).normalize();
            const labelWorldPos = nodeWorldPos.clone().addScaledVector(toCamera, worldRadius + GAP);

            const localPos = textEl.object3D.parent.worldToLocal(labelWorldPos);
            textEl.object3D.position.copy(localPos);
            textEl.object3D.lookAt(cameraPos);
        });
    },

    _destroyLabels: function () {
        if (this.labelsContainer) {
            this.labelsContainer.remove();
            this.labelsContainer = null;
        }
        this.labelData = [];
        this._sectorLabelEls = [];
    },

    _isAutoRotateEnabled: function () {
        const wrapper = document.getElementById('news-graph-wrapper');
        const rotComp = wrapper && wrapper.components['news-graph-rotation'];
        if (rotComp && typeof rotComp.isAutoRotateEnabled === 'function') {
            return rotComp.isAutoRotateEnabled();
        }
        return true;
    },

    _getOuterGroupEl: function () {
        return null;
    },

    _tickAutoRotate: function (timeDelta) {
        if (!this._isAutoRotateEnabled()) return;
        const ROT_SPEED = 0.0006;
        const dt = Math.min(timeDelta, 100);

        if (this._sectorEl && this._sectorEl.object3D) {
            this._sectorEl.object3D.rotation.z -= ROT_SPEED;
        }
        if (this._layout) {
            const M = this._layout.sortedTickerNodes.length;
            this._layout.sortedTickerNodes.forEach((node, i) => {
                const el = this.meshMap[node.id];
                if (!el) return;
                const baseAngle = (i / Math.max(M, 1)) * Math.PI * 2;
                const curZ = this._sectorEl && this._sectorEl.object3D
                    ? this._sectorEl.object3D.rotation.z : 0;
                const angle = baseAngle + curZ;
                const OUTER_R = 4.0;
                el.object3D.position.set(OUTER_R * Math.cos(angle), OUTER_R * Math.sin(angle), 0);
                el.object3D.rotation.z = el.userData.staticConeRotation || 0;
            });

            const N   = this._layout.narrativeNodes.length;
            const innerRotZ = this._innerRotZ = (this._innerRotZ || 0) + ROT_SPEED;
            this._layout.narrativeNodes.forEach((node, j) => {
                const el = this.meshMap[node.id];
                if (!el) return;
                const baseAngle = (j / Math.max(N, 1)) * Math.PI * 2;
                const angle     = baseAngle + innerRotZ;
                const INNER_R   = 2.0;
                el.object3D.position.set(INNER_R * Math.cos(angle), INNER_R * Math.sin(angle), 0);
            });
        }
    },

    _adjustScaleToFit: function () {
        const maxDist = 4.35 + 0.6;
        const desiredRadius = 0.40;
        const scale = desiredRadius / maxDist;
        this.el.setAttribute('scale', `${scale} ${scale} ${scale}`);

        if (this.labelsContainer) {
            const inv = 1 / scale;
            this.labelsContainer.setAttribute('scale', `${inv} ${inv} ${inv}`);
        }
    },

    setSelectedNodeEl: function (meshEl, node, triggerEvent = true) {
        if (meshEl === this.selectedNodeEl && meshEl) {
            this.selectedNodeEl = null;
        } else {
            this.selectedNodeEl = meshEl;
        }

        if (window.VRPanelsSlidingManager) {
            window.VRPanelsSlidingManager.setSelectedEl(this.selectedNodeEl);
        }

        this._updateNodeVisuals();

        if (triggerEvent) {
            this.el.sceneEl.emit('news-graph-node-clicked', {
                node: this.selectedNodeEl ? node : null,
                data: this.graphData,
            });
        }
    },

    selectNodeById: function (nodeId, triggerEvent = true) {
        if (!this.nodes || !this.meshMap) return;
        const normalizedId = String(nodeId).toUpperCase().trim();

        let foundNode = null;
        let foundMesh = null;

        for (const node of this.nodes) {
            const nodeIdUp = String(node.id || '').toUpperCase();
            const symUp    = String(node.symbol || '').toUpperCase();
            if (nodeIdUp === normalizedId || symUp === normalizedId) {
                foundNode = node;
                foundMesh = this.meshMap[node.id] || null;
                break;
            }
        }

        this.setSelectedNodeEl(foundMesh || null, foundNode, triggerEvent);
    },

    _isLightTheme: function (evt) {
        if (evt && evt.detail && evt.detail.themeMode) {
            return evt.detail.themeMode === 'Light';
        }
        const body = document.body;
        if (body && (body.classList.contains('light') || body.classList.contains('cb-light'))) {
            return true;
        }
        if (typeof getCacheItem === 'function') {
            const mainSettings = getCacheItem('mainSettings');
            if (mainSettings && mainSettings.themeMode === 'Light') return true;
        }
        return false;
    },

    _attachThemeListener: function () {
        if (this._themeListenerAttached) return;
        this._themeListenerAttached = true;
        const scene = this.el.sceneEl || document.querySelector('a-scene');
        this._onThemeChanged = (evt) => {
            this._updateLabelTheme(evt);
        };
        if (scene) {
            scene.addEventListener('theme-changed', this._onThemeChanged);
            scene.addEventListener('app-state-changed', this._onThemeChanged);
        }
        document.addEventListener('theme-changed', this._onThemeChanged);
        window.addEventListener('theme-changed', this._onThemeChanged);
    },

    _updateLabelTheme: function (evt) {
        const isLight = this._isLightTheme(evt);
        const nodeTextColor = isLight ? '#111827' : '#ffffff';
        const nodeOutlineColor = isLight ? '#ffffff' : '#000000';

        if (this.labelData) {
            this.labelData.forEach(({ textEl }) => {
                if (!textEl) return;
                textEl.setAttribute('color', nodeTextColor);
                textEl.setAttribute('outline-color', nodeOutlineColor);
            });
        }

        if (this._sectorLabelEls) {
            this._sectorLabelEls.forEach(({ textEl, arc }) => {
                if (!textEl) return;
                textEl.setAttribute('outline-color', isLight ? '#ffffff' : '#000000');
            });
        }
    },

    tick: function (time, timeDelta) {
        this._nodeClickedThisFrame = false;

        if (!this.graphData) return;

        this._tickAutoRotate(timeDelta);
        this._updateEdges2D();
        this._updateVRLabels();
        this._updateSectorLabels();
        window.NewsGraph.updateGlowPulse(Object.values(this.meshMap || {}), time, 1.1, 1.6);
    },
});
